<script lang="ts">
  import { isNonEmptyArray } from '#app/lib/boolean_tests'
  import WrapToggler from '#components/wrap_toggler.svelte'
  import ClaimsInfobox from '#entities/components/layouts/claims_infobox.svelte'
  import EntityClaimsLinks from '#entities/components/layouts/entity_claims_links.svelte'
  import { infoboxShortlistPropertiesByType, infoboxPropertiesByType } from '#entities/components/lib/claims_helpers'
  import { omitClaims } from '#entities/components/lib/work_helpers'
  import type { SerializedEntitiesByUris } from '#entities/lib/entities'
  import Spinner from '#general/components/spinner.svelte'
  import type { ExtendedEntityType, PropertyUri, SimplifiedClaims } from '#server/types/entity'
  import { I18n } from '#user/lib/i18n'

  const {
    claims = {},
    omittedProperties = [],
    relatedEntities = {},
    entityType,
    shortlistOnly = false,
    listDisplay = false,
  }: {
    claims?: SimplifiedClaims
    omittedProperties?: PropertyUri[]
    relatedEntities?: SerializedEntitiesByUris
    entityType: ExtendedEntityType
    shortlistOnly?: boolean
    listDisplay?: boolean
  } = $props()

  const infoboxPropertiesToDisplay: PropertyUri[] = $derived(infoboxPropertiesByType[entityType] || [])

  const allowlistedProperties: PropertyUri[] = $derived.by(() => {
    if (shortlistOnly) {
      return infoboxShortlistPropertiesByType[entityType] || infoboxPropertiesToDisplay
    } else {
      return infoboxPropertiesToDisplay
    }
  })

  let showDetails = $state(false)
  let infobox: HTMLElement = $state()
  const infoboxHeight = $derived(infobox?.clientHeight)
  let waitingForEntities: Promise<void> = $state()
  const wrappedInfoboxHeight = 128

  const wrappedSize = $derived(listDisplay && infoboxHeight && infoboxHeight > wrappedInfoboxHeight)
  const displayedClaims = $derived(omitClaims(claims, omittedProperties))
</script>

<div class="claims-infobox-wrapper">
  <div
    bind:this={infobox}
    class:wrapped-size={wrappedSize && !showDetails}
    class="claims-infobox"
  >
    <ClaimsInfobox
      {allowlistedProperties}
      claims={displayedClaims}
      {relatedEntities}
      {entityType}
      bind:waitingForEntities
    />
    {#if !shortlistOnly}
      <EntityClaimsLinks {claims} />
    {/if}
  </div>
  {#if isNonEmptyArray(allowlistedProperties)}
    {#await waitingForEntities}
      <Spinner />
    {/await}
  {/if}
  {#if wrappedSize}
    <WrapToggler
      bind:show={showDetails}
      moreText={I18n('more details')}
      lessText={I18n('less details')}
    />
  {/if}
</div>

<style lang="scss">
  @import "general/scss/utils";
  .claims-infobox{
    flex: 3 0 0;
  }
  .wrapped-size{
    // in sync with wrappedInfoboxHeight variable
    block-size: 125px;
    overflow: hidden;
  }
</style>
